import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StudioProvider } from "@/context/StudioContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import AllStudios from "@/pages/AllStudios";
import CreateStudio from "@/pages/CreateStudio";
import StudioBible from "@/pages/StudioBible";
import CharacterStudio from "@/pages/CharacterStudio";
import CharacterBible from "@/pages/CharacterBible";
import Outfits from "@/pages/Outfits";
import WorldBuilder from "@/pages/WorldBuilder";
import StoryAI from "@/pages/StoryAI";
import Canon from "@/pages/Canon";
import AIMemory from "@/pages/AIMemory";
import EpisodeStudio from "@/pages/EpisodeStudio";
import ScriptEditor from "@/pages/ScriptEditor";
import Storyboard from "@/pages/Storyboard";
import AIDirector from "@/pages/AIDirector";
import TimelineEditor from "@/pages/TimelineEditor";
import VoiceStudio from "@/pages/VoiceStudio";
import Localization from "@/pages/Localization";
import Shorts from "@/pages/Shorts";
import Thumbnails from "@/pages/Thumbnails";
import Music from "@/pages/Music";
import Social from "@/pages/Social";
import Publisher from "@/pages/Publisher";
import ContentCalendar from "@/pages/ContentCalendar";
import Analytics from "@/pages/Analytics";
import Growth from "@/pages/Growth";
import Revenue from "@/pages/Revenue";
import CostControl from "@/pages/CostControl";
import Providers from "@/pages/Providers";
import AIAgents from "@/pages/AIAgents";
import GameStudio from "@/pages/GameStudio";
import AppStudio from "@/pages/AppStudio";
import PinPilotCampaignStudio from "@/pages/PinPilotCampaignStudio";
import RenderPipeline from "@/pages/RenderPipeline";
import AssetLibrary from "@/pages/AssetLibrary";
import CostDashboard from "@/pages/CostDashboard";
import HypeRadar from "@/pages/HypeRadar";
import SetupCenter from "@/pages/SetupCenter";
import LocalAI from "@/pages/LocalAI";
import LocalTestLab from "@/pages/LocalTestLab";
import SonicIdentity from "@/pages/SonicIdentity";
import UsersAccess from "@/pages/UsersAccess";
import MyAccount from "@/pages/MyAccount";
import Trash from "@/pages/Trash";
import AuditLog from "@/pages/AuditLog";
import Safety from "@/pages/Safety";
import Provenance from "@/pages/Provenance";
import Licensing from "@/pages/Licensing";
import Merch from "@/pages/Merch";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen bg-[#050505] flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={!loading && user ? <Navigate to="/" replace /> : <Login />} />
      <Route element={<Protected><StudioProvider><Layout /></StudioProvider></Protected>}>
        <Route path="/" element={<Home />} />
        <Route path="/studios" element={<AllStudios />} />
        <Route path="/studios/new" element={<CreateStudio />} />
        <Route path="/studio-bible" element={<StudioBible />} />
        <Route path="/characters" element={<CharacterStudio />} />
        <Route path="/bible" element={<CharacterBible />} />
        <Route path="/outfits" element={<Outfits />} />
        <Route path="/worlds" element={<WorldBuilder />} />
        <Route path="/story" element={<StoryAI />} />
        <Route path="/canon" element={<Canon />} />
        <Route path="/memory" element={<AIMemory />} />
        <Route path="/episodes" element={<EpisodeStudio />} />
        <Route path="/script" element={<ScriptEditor />} />
        <Route path="/storyboard" element={<Storyboard />} />
        <Route path="/director" element={<AIDirector />} />
        <Route path="/timeline" element={<TimelineEditor />} />
        <Route path="/voice" element={<VoiceStudio />} />
        <Route path="/localization" element={<Localization />} />
        <Route path="/shorts" element={<Shorts />} />
        <Route path="/thumbnails" element={<Thumbnails />} />
        <Route path="/music" element={<Music />} />
        <Route path="/social" element={<Social />} />
        <Route path="/publisher" element={<Publisher />} />
        <Route path="/calendar" element={<ContentCalendar />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/growth" element={<Growth />} />
        <Route path="/revenue" element={<Revenue />} />
        <Route path="/costs" element={<CostControl />} />
        <Route path="/providers" element={<Providers />} />
        <Route path="/agents" element={<AIAgents />} />
        <Route path="/games" element={<GameStudio />} />
        <Route path="/apps" element={<AppStudio />} />
        <Route path="/campaigns/pinpilot" element={<PinPilotCampaignStudio />} />
        <Route path="/render" element={<RenderPipeline />} />
        <Route path="/assets" element={<AssetLibrary />} />
        <Route path="/cost-dashboard" element={<CostDashboard />} />
        <Route path="/hype-radar" element={<HypeRadar />} />
        <Route path="/setup" element={<SetupCenter />} />
        <Route path="/local" element={<LocalAI />} />
        <Route path="/local-lab" element={<LocalTestLab />} />
        <Route path="/sonic" element={<SonicIdentity />} />
        <Route path="/users-access" element={<UsersAccess />} />
        <Route path="/account" element={<MyAccount />} />
        <Route path="/trash" element={<Trash />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/provenance" element={<Provenance />} />
        <Route path="/licensing" element={<Licensing />} />
        <Route path="/merch" element={<Merch />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster theme="dark" position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
